import {
	VISITOR_THEME_CHOICE_STORAGE_KEY,
	VISITOR_THEME_DEFAULT_ATTR,
	VISITOR_THEME_PERSIST_ATTR,
	VISITOR_THEME_SWITCHER_ATTR,
} from "@multi-tenant-cms/builder/theme/visitorThemeModes";

/**
 * Inline pre-paint boot script — rendered first in `<head>` by
 * `ThemeBootScript` (`layout.tsx`) so the browser runs it during HTML parse,
 * and can flip `<html class="dark">`, before paint (no flash of the wrong
 * theme). Client navigations do not render the tag. It reads three
 * attributes SSR already put on `<html>` (never trusts stored/OS state for
 * `lightOnly`/`darkOnly`):
 *
 * | `data-theme-default` | `data-theme-switcher` | Result |
 * | --- | --- | --- |
 * | `light` | `0` | always light — never reads storage |
 * | `dark`  | `0` | always dark — never reads storage |
 * | `system` or switcher `1` | — | "both modes live": a stored choice wins (only when switcher AND persist are both `1`), else `system` follows `prefers-color-scheme`, else (switcher on, no stored choice yet) starts from `data-theme-default` |
 *
 * When switcher+persist aren't both on, any leftover `localStorage` entry is
 * actively cleared (not just ignored) so it can never resurrect later — e.g.
 * an editor turning the switcher back on shouldn't suddenly honour a stale
 * pick from when it was last on.
 *
 * Kept as one dependency-free string (no imports at runtime, no bundler
 * output) — it must execute before any React/hydration code loads.
 */
export const THEME_BOOT_SCRIPT = `(function () {
  try {
    var docEl = document.documentElement;
    var mode = docEl.getAttribute('${VISITOR_THEME_DEFAULT_ATTR}') || 'system';
    var switcherOn = docEl.getAttribute('${VISITOR_THEME_SWITCHER_ATTR}') === '1';
    var persistOn = docEl.getAttribute('${VISITOR_THEME_PERSIST_ATTR}') === '1';
    var storageKey = '${VISITOR_THEME_CHOICE_STORAGE_KEY}';
    var canUseStoredChoice = switcherOn && persistOn;
    var stored = null;
    if (canUseStoredChoice) {
      try {
        stored = window.localStorage.getItem(storageKey);
      } catch (e) {}
    } else {
      // Not eligible for a persisted choice right now (switcher off, or
      // persist off) — actively clear any leftover value so it can never
      // resurrect later (e.g. an editor re-enabling the switcher shouldn't
      // suddenly honour a stale pick from when it was last on).
      try {
        window.localStorage.removeItem(storageKey);
      } catch (e) {}
    }
    var isDark;
    if (mode === 'light' && !switcherOn) {
      isDark = false;
    } else if (mode === 'dark' && !switcherOn) {
      isDark = true;
    } else if (stored === 'light' || stored === 'dark') {
      isDark = stored === 'dark';
    } else if (mode === 'system') {
      isDark = !!(
        window.matchMedia &&
        window.matchMedia('(prefers-color-scheme: dark)').matches
      );
    } else {
      isDark = mode === 'dark';
    }
    if (isDark) {
      docEl.classList.add('dark');
    } else {
      docEl.classList.remove('dark');
    }
  } catch (e) {}
})();`;
